/**
 * 权限工具函数
 * 用于处理模块配置系统中的权限
 * @author AI Assistant
 * @date 2024-07-09
 */

import { auth } from '@/auth'
import type { Permission as AuthPermission } from '@/auth/types'
import { loadAllModuleConfigs } from './module-config'

/**
 * 从模块配置中提取权限
 * @returns 权限列表
 */
export async function extractPermissionsFromModules(): Promise<AuthPermission[]> {
  const moduleConfigs = await loadAllModuleConfigs()
  const permissions: AuthPermission[] = []

  for (const config of moduleConfigs) {
    if (!config.enabled) continue

    // 提取模块权限
    for (const permission of config.permissions || []) {
      {
        const [module, , action] = permission.code.split(':')
        permissions.push({
          id: permission.code,
          code: permission.code,
          name: permission.name,
          type: (permission.type === 'operation' ? 'button' : permission.type) as AuthPermission['type'],
          description: permission.description,
          group: module,
          enabled: true,
          resource: module,
          action,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      }
    }

    // 提取路由权限
    if (config.routes) {
      const routePermissions = extractRoutePermissions(config.routes, config.id)
      permissions.push(...routePermissions)
    }
  }

  return permissions
}

/**
 * 从路由配置中提取权限
 * @param routes 路由配置
 * @param moduleId 模块ID
 * @returns 权限列表
 */
function extractRoutePermissions(routes: any[], moduleId: string): PermissionType[] {
  const permissions: PermissionType[] = []

  function traverse(items: any[]) {
    for (const item of items) {
      // 如果路由项有权限配置，则添加到权限列表
      if (item.meta?.permission) {
        const permCode = typeof item.meta.permission === 'string'
          ? item.meta.permission
          : item.meta.permission.code

        if (permCode) {
          permissions.push({
            id: permCode,
            code: permCode,
            name: `访问${item.meta.title || item.name || ''}`,
            type: 'menu',
            description: `访问${item.meta.title || item.name || ''}页面`,
            group: moduleId,
            enabled: true,
            resource: moduleId,
            action: 'view',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        }
      }

      // 递归处理子路由
      if (item.children && item.children.length > 0) {
        traverse(item.children)
      }
    }
  }

  traverse(routes)
  return permissions
}

/**
 * 初始化权限管理器
 * @param manager 权限管理器实例
 */
export async function initializePermissionManager(manager = auth.permissionManager): Promise<void> {
  const permissions = await extractPermissionsFromModules()
  manager.setPermissions(permissions)

}

/**
 * 检查用户是否拥有指定模块的权限
 * @param moduleId 模块ID
 * @param action 操作名称
 * @param authManager 权限管理器实例
 * @returns 是否拥有权限
 */
export function hasModulePermission(
  moduleId: string,
  action: string,
  authManager = auth.permissionManager
): boolean {
  // 构建权限代码
  const permissionCode = `${moduleId}:*:${action}`

  // 检查权限
  return authManager.hasPermission(permissionCode, { strict: false })
}

/**
 * 检查用户是否拥有指定角色
 * @param role 角色代码
 * @param authManager 权限管理器实例
 * @returns 是否拥有角色
 */
export function hasRole(
  role: string | string[],
  authManager = auth.permissionManager
): boolean {
  const roles = Array.isArray(role) ? role : [role]
  return authManager.hasRoles(roles, 'any')
}

/**
 * 获取用户在指定模块的所有权限
 * @param moduleId 模块ID
 * @param authManager 权限管理器实例
 * @returns 权限列表
 */
export function getModulePermissions(
  moduleId: string,
  authManager = auth.permissionManager
): string[] {
  const allPermissions = authManager.getUserPermissions()
  return allPermissions.filter(p => p.startsWith(`${moduleId}:`))
}

/**
 * 获取用户所有权限
 * @param authManager 权限管理器实例
 * @returns 权限列表，按类型分组
 */
export function getAllUserPermissions(authManager = auth.permissionManager) {
  const allPermissions = authManager.getUserPermissions()
  const allPermissionObjects = allPermissions
    .map(code => authManager.getPermissionDefinition(code))
    .filter(Boolean)

  // 按类型分组
  const result = {
    routes: [] as string[],
    buttons: [] as string[],
    apis: [] as string[]
  }

  // 遍历所有权限，根据类型分组
  allPermissionObjects.forEach((permission) => {
    if (!permission) return

    switch (permission.type) {
      case 'menu':
        result.routes.push(permission.code)
        break
      case 'button':
        result.buttons.push(permission.code)
        break
      case 'api':
        result.apis.push(permission.code)
        break
    }
  })

  return result
}

/**
 * 检查用户是否为超级管理员
 * @param authManager 权限管理器实例
 * @returns 是否为超级管理员
 */
export function isSuperAdmin(authManager = auth.permissionManager): boolean {
  return authManager.isSuperAdmin()
}

/**
 * 检查用户是否为租户管理员
 * @param authManager 权限管理器实例
 * @returns 是否为租户管理员
 */
export function isTenantAdmin(authManager = auth.permissionManager): boolean {
  return authManager.hasRole('tenant_admin')
}
